import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import _ from 'lodash';
import { useState } from 'react';
import { Else, If, Then } from 'react-if';

import { stringAvatar } from '@/Helper/stringAvatar';

type OverflowChipsCellProps = {
  value?: string[];
  maxVisible?: number;
  onItemClick?: (item: string)=> void;
  avatarByItem?: Record<string, string>;
};

export default function OverflowChipsCell({
  value,
  maxVisible = 8,
  onItemClick,
  avatarByItem,
}: OverflowChipsCellProps): JSX.Element {
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  const stringValues = _(value ?? [])
    .map((item) => item.trim())
    .filter((item) => item !== '' && item !== '—')
    .uniq()
    .value();

  const visibleValues = stringValues.slice(0, maxVisible);
  const overflowValues = stringValues.slice(maxVisible);
  const isOverflowOpen = Boolean(popoverAnchor);
  const buildChip = (item: string, closeOnClick = false): JSX.Element => {
    const normalizedItem = item.trim().toLowerCase();
    const assignedResourceName = avatarByItem?.[normalizedItem]?.trim();
    let avatar: JSX.Element | undefined;

    if (assignedResourceName) {
      const avatarProps = stringAvatar(assignedResourceName);
      avatar = (
        <Avatar
          {...avatarProps}
          sx={{
            ...avatarProps.sx,
            width: 22,
            height: 22,
            fontSize: 11,
          }}
        />
      );
    }

    return (
      <Chip
        key={item}
        size='small'
        label={item}
        avatar={avatar}
        clickable={!!onItemClick}
        onClick={onItemClick ? () => {
          onItemClick(item);
          if (closeOnClick) setPopoverAnchor(null);
        } : undefined}
      />
    );
  };

  return (
    <If condition={value == null}>
      <Then>
        <CircularProgress size={16} />
      </Then>
      <Else>
        <If condition={!stringValues.length}>
          <Then>—</Then>
          <Else>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {visibleValues.map((item) => buildChip(item))}

              <If condition={overflowValues.length > 0}>
                <Then>
                  <>
                    <Chip
                      size='small'
                      label={`+${overflowValues.length} more`}
                      onClick={(e) => setPopoverAnchor(e.currentTarget)}
                    />
                    <Popover
                      open={isOverflowOpen}
                      anchorEl={popoverAnchor}
                      onClose={() => setPopoverAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      <Box
                        sx={{
                          p: 1.25,
                          width: 360,
                          maxWidth: 'min(92vw, 360px)',
                          maxHeight: 340,
                          overflowY: 'auto',
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'flex-start',
                          gap: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        {overflowValues.map((item) => buildChip(item, true))}
                      </Box>
                    </Popover>
                  </>
                </Then>
              </If>
            </Box>
          </Else>
        </If>
      </Else>
    </If>
  );
}
